"use client";

import React from "react";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";

import {
  AppModalConfig,
  AppModalEntry,
  AppModalKey,
  AppModalsContextType,
  AppModalsState,
} from "./AppModals.types";

const AppModalsContext = React.createContext<AppModalsContextType | null>(null);

/** The provider's state and writer; read it through `useAppModal`, not directly. */
const useAppModalsContext = () => {
  const context = React.useContext(AppModalsContext);

  if (!context) {
    throw new Error("useAppModal must be used within an AppModalsProvider");
  }

  return context;
};

/**
 * Each modal's config; its component loads on its first open. The Record makes a new
 * `AppModalKey` fail to compile until its config is added.
 */
const APP_MODALS: Record<AppModalKey, AppModalConfig> = {
  // Global
  [AppModalKey.CloudinaryUploadModal]: {
    component: dynamic(
      () =>
        import("./CloudinaryUploadModal/CloudinaryUploadModal").then(
          (module) => module.CloudinaryUploadModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ImageGalleryModal]: {
    component: dynamic(
      () =>
        import("./ImageGalleryModal/ImageGalleryModal").then(
          (module) => module.ImageGalleryModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.PendingReviewsModal]: {
    component: dynamic(
      () =>
        import("./PendingReviewsModal/PendingReviewsModal").then(
          (module) => module.PendingReviewsModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.PhoneVerificationModal]: {
    component: dynamic(
      () =>
        import("./PhoneVerificationModal/PhoneVerificationModal").then(
          (module) => module.PhoneVerificationModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.PhotoViewerModal]: {
    component: dynamic(
      () =>
        import("./PhotoViewerModal/PhotoViewerModal").then(
          (module) => module.PhotoViewerModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ProfileModal]: {
    component: dynamic(
      () =>
        import("./ProfileModal/ProfileModal").then(
          (module) => module.ProfileModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ShareModal]: {
    component: dynamic(
      () =>
        import("./ShareModal/ShareModal").then((module) => module.ShareModal),
      { ssr: false },
    ),
  },
  [AppModalKey.VerifyEmailModal]: {
    component: dynamic(
      () =>
        import("./VerifyEmailModal/VerifyEmailModal").then(
          (module) => module.VerifyEmailModal,
        ),
      { ssr: false },
    ),
  },

  // Home
  [AppModalKey.HomeCompactSearchTray]: {
    component: dynamic(
      () =>
        import("./HomeCompactSearchTray/HomeCompactSearchTray").then(
          (module) => module.HomeCompactSearchTray,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.HomeHeroFieldTray]: {
    component: dynamic(
      () =>
        import("./HomeHeroFieldTray/HomeHeroFieldTray").then(
          (module) => module.HomeHeroFieldTray,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.HomePromoEntitlementsModal]: {
    component: dynamic(
      () =>
        import("./HomePromoEntitlementsModal/HomePromoEntitlementsModal").then(
          (module) => module.HomePromoEntitlementsModal,
        ),
      { ssr: false },
    ),
  },

  // Host
  [AppModalKey.HostInterstitialModal]: {
    component: dynamic(
      () =>
        import("./HostInterstitialModal/HostInterstitialModal").then(
          (module) => module.HostInterstitialModal,
        ),
      { ssr: false },
    ),
  },

  // Listings
  [AppModalKey.ListingAddOnModal]: {
    component: dynamic(
      () =>
        import("./ListingAddOnModal/ListingAddOnModal").then(
          (module) => module.ListingAddOnModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingAiAnswersModal]: {
    component: dynamic(
      () =>
        import("./ListingAiAnswersModal/ListingAiAnswersModal").then(
          (module) => module.ListingAiAnswersModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingAwardsWinnerDrawer]: {
    component: dynamic(
      () =>
        import("./ListingAwardsWinnerDrawer/ListingAwardsWinnerDrawer").then(
          (module) => module.ListingAwardsWinnerDrawer,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingFsvMenuModal]: {
    component: dynamic(
      () =>
        import("./ListingFsvMenuModal/ListingFsvMenuModal").then(
          (module) => module.ListingFsvMenuModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingPhotoDownloadModal]: {
    component: dynamic(
      () =>
        import("./ListingPhotoDownloadModal/ListingPhotoDownloadModal").then(
          (module) => module.ListingPhotoDownloadModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingPriceDetailsDrawer]: {
    component: dynamic(
      () =>
        import("./ListingPriceDetailsDrawer/ListingPriceDetailsDrawer").then(
          (module) => module.ListingPriceDetailsDrawer,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingPriceDetailsModal]: {
    component: dynamic(
      () =>
        import("./ListingPriceDetailsModal/ListingPriceDetailsModal").then(
          (module) => module.ListingPriceDetailsModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingPricingRulesModal]: {
    component: dynamic(
      () =>
        import("./ListingPricingRulesModal/ListingPricingRulesModal").then(
          (module) => module.ListingPricingRulesModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingRecommendationsModal]: {
    component: dynamic(
      () =>
        import("./ListingRecommendationsModal/ListingRecommendationsModal").then(
          (module) => module.ListingRecommendationsModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ListingSaveModal]: {
    component: dynamic(
      () =>
        import("./ListingSaveModal/ListingSaveModal").then(
          (module) => module.ListingSaveModal,
        ),
      { ssr: false },
    ),
  },

  // Account profile
  [AppModalKey.ProfileNameModal]: {
    component: dynamic(
      () =>
        import("./ProfileNameModal/ProfileNameModal").then(
          (module) => module.ProfileNameModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.ProfileOccupationModal]: {
    component: dynamic(
      () =>
        import("./ProfileOccupationModal/ProfileOccupationModal").then(
          (module) => module.ProfileOccupationModal,
        ),
      { ssr: false },
    ),
  },

  // Host profiles
  [AppModalKey.ProfilesMessageHostModal]: {
    component: dynamic(
      () =>
        import("./ProfilesMessageHostModal/ProfilesMessageHostModal").then(
          (module) => module.ProfilesMessageHostModal,
        ),
      { ssr: false },
    ),
  },

  // Vendors
  [AppModalKey.VendorAboutModal]: {
    component: dynamic(
      () =>
        import("./VendorAboutModal/VendorAboutModal").then(
          (module) => module.VendorAboutModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorAddVideosModal]: {
    component: dynamic(
      () =>
        import("./VendorAddVideosModal/VendorAddVideosModal").then(
          (module) => module.VendorAddVideosModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorContactSheet]: {
    component: dynamic(
      () =>
        import("./VendorContactSheet/VendorContactSheet").then(
          (module) => module.VendorContactSheet,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorDeleteMediaModal]: {
    component: dynamic(
      () =>
        import("./VendorDeleteMediaModal/VendorDeleteMediaModal").then(
          (module) => module.VendorDeleteMediaModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorDocumentModal]: {
    component: dynamic(
      () =>
        import("./VendorDocumentModal/VendorDocumentModal").then(
          (module) => module.VendorDocumentModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorMediaTypeModal]: {
    component: dynamic(
      () =>
        import("./VendorMediaTypeModal/VendorMediaTypeModal").then(
          (module) => module.VendorMediaTypeModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorPricingModal]: {
    component: dynamic(
      () =>
        import("./VendorPricingModal/VendorPricingModal").then(
          (module) => module.VendorPricingModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorReplacePhotoModal]: {
    component: dynamic(
      () =>
        import("./VendorReplacePhotoModal/VendorReplacePhotoModal").then(
          (module) => module.VendorReplacePhotoModal,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorTipsSheet]: {
    component: dynamic(
      () =>
        import("./VendorTipsSheet/VendorTipsSheet").then(
          (module) => module.VendorTipsSheet,
        ),
      { ssr: false },
    ),
  },
  [AppModalKey.VendorUploadMediaDrawer]: {
    component: dynamic(
      () =>
        import("./VendorUploadMediaDrawer/VendorUploadMediaDrawer").then(
          (module) => module.VendorUploadMediaDrawer,
        ),
      { ssr: false },
    ),
  },
};

/**
 * Holds which app-wide modals are open, and with what props, and renders each one from
 * its first opening until it is removed. Everything else opens and closes them through
 * `useAppModal`. A route change removes them all.
 */
const AppModalsProvider = ({ children }: React.PropsWithChildren) => {
  const pathname = usePathname();
  // Every modal's slot, `{ modalKey: { isOpen, openingId, props } }`; no slot means the
  // modal is not mounted.
  const [modals, setModals] = React.useState<AppModalsState>({});
  const [modalsPathname, setModalsPathname] = React.useState(pathname);

  // The provider outlives the page, so a client-side navigation (a same-tab link, or
  // Back) would leave the page's modals open, holding its props and callbacks, on the
  // next one. Reset during render rather than in an effect, so the next page's own
  // mount effects can still open a modal.
  if (pathname !== modalsPathname) {
    setModalsPathname(pathname);
    setModals({});
  }

  // Hands out opening ids. It lives outside the state so a removal, which drops the
  // slot, cannot reset it: a remove and a reopen batched together would otherwise reuse
  // the rendered key and keep the old modal's state.
  const lastOpeningIdRef = React.useRef(0);

  const openModal: AppModalsContextType["openModal"] = (modal, props) => {
    // Taken outside the updater, which React may run twice.
    lastOpeningIdRef.current += 1;
    const openingId = lastOpeningIdRef.current;

    setModals((current) => {
      // The state is keyed loosely; narrow to this modal's slot.
      const entry = current[modal] as AppModalEntry<typeof modal> | undefined;
      const isOpen = entry?.isOpen ?? false;
      // An updater sees the props of an open modal only; a closing one is on its way out.
      const nextProps =
        typeof props === "function"
          ? props(isOpen ? entry?.props : undefined)
          : props;

      // Same props back for an open modal: keep the state object so nothing re-renders.
      if (entry && isOpen && nextProps === entry.props) {
        return current;
      }

      return {
        ...current,
        [modal]: {
          isOpen: true,
          // Only an opening from closed takes a new id, so re-pushing props keeps the state.
          openingId: isOpen && entry ? entry.openingId : openingId,
          props: nextProps,
        },
      };
    });
  };

  const closeModal: AppModalsContextType["closeModal"] = (modal) => {
    setModals((current) => {
      const entry = current[modal];

      // Closed already, or never opened: nothing to animate out.
      if (!entry?.isOpen) {
        return current;
      }

      return { ...current, [modal]: { ...entry, isOpen: false } };
    });
  };

  const removeModal: AppModalsContextType["removeModal"] = (modal) => {
    setModals((current) => {
      if (!current[modal]) {
        return current;
      }

      const { [modal]: _removed, ...rest } = current;
      return rest;
    });
  };

  const value = { closeModal, modals, openModal, removeModal };

  return (
    <AppModalsContext.Provider value={value}>
      {children}
      {/* A modal mounts on its first opening, so its chunk downloads then. It stays
          mounted while closed, so it can animate out, and is keyed by its opening, so
          each opening starts with fresh state. */}
      {Object.values(AppModalKey).map((modalKey) => {
        const entry = modals[modalKey];

        if (!entry) {
          return null;
        }

        const ModalComponent = APP_MODALS[modalKey].component;
        return <ModalComponent key={`${modalKey}:${entry.openingId}`} />;
      })}
    </AppModalsContext.Provider>
  );
};

export { AppModalsProvider, useAppModalsContext };
